import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Calendar, ChevronRight, BookOpen, Send, Sparkles, MessageCircle, Check, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const GRADES = [6, 7, 8, 9, 10, 11, 12];
const BOARDS = ['CBSE', 'ICSE', 'State Board', 'Other'];

export default function ScoreContractModal({ isOpen, onClose, onNavigate }) {
  const { api, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  // Step 1: Profile setup
  const [profile, setProfile] = useState({
    childName: user?.name || '',
    grade: user?.class_name ? parseInt(user.class_name.replace('Class ', '')) : 10,
    board: 'CBSE',
    examDate: ''
  });
  
  // Step 2: Target & Chapters
  const [targetScore, setTargetScore] = useState(75);
  const [coveredChapters, setCoveredChapters] = useState({});
  
  // Step 3: Parent notification
  const [parentPhone, setParentPhone] = useState('');
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (isOpen && step === 2) {
      fetchSubjectsAndChapters();
    }
  }, [isOpen, step, profile.grade]);

  const fetchSubjectsAndChapters = async () => {
    try {
      const className = `Class ${profile.grade}`;
      const subjectsRes = await api.get('/subjects');
      setSubjects(subjectsRes.data || []);
      
      // Fetch chapters for all subjects
      const allChapters = {};
      for (const subject of (subjectsRes.data || [])) {
        try {
          const chaptersRes = await api.get(`/chapters?subject=${subject.name}&class_name=${className}`);
          // Sort chapters by number
          const sortedChapters = (chaptersRes.data || []).sort((a, b) => a.number - b.number);
          allChapters[subject.name] = sortedChapters;
        } catch (err) {
          console.debug(`No chapters for ${subject.name}`);
        }
      }
      setChapters(allChapters);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const toggleChapter = (subject, chapterNumber) => {
    setCoveredChapters(prev => {
      const subjectChapters = prev[subject] || [];
      if (subjectChapters.includes(chapterNumber)) {
        return { ...prev, [subject]: subjectChapters.filter(n => n !== chapterNumber) };
      } else {
        return { ...prev, [subject]: [...subjectChapters, chapterNumber] };
      }
    });
  };

  const getSubjectCoveredCount = (subject) => {
    return (coveredChapters[subject] || []).length;
  };

  const getTotalChapterCount = (subject) => {
    return (chapters[subject] || []).length;
  };

  const handleStep1Continue = () => {
    if (!profile.childName || !profile.examDate) return;
    setStep(2);
  };

  const handleStep2Continue = async () => {
    setLoading(true);
    try {
      // Update user profile with class and board
      await api.patch('/user/profile', {
        name: profile.childName,
        class_name: `Class ${profile.grade}`,
        board: profile.board
      });

      // Save the primary exam goal
      await api.post('/exams/subject-wise', {
        subject: 'All Subjects',
        exam_name: `${profile.board} Board Exam`,
        exam_date: profile.examDate,
        target_score: targetScore,
        syllabus_chapters: Object.values(coveredChapters).flat(),
        is_primary_goal: true
      });

      // Calculate plan
      const today = new Date();
      const examDate = new Date(profile.examDate);
      const daysUntilExam = Math.ceil((examDate - today) / (1000 * 60 * 60 * 24));
      const totalChapters = Object.values(coveredChapters).flat().length;
      const sessionsPerWeek = Math.min(7, Math.max(3, Math.ceil(totalChapters / Math.max(1, daysUntilExam / 7))));
      
      setPlan({
        daysUntilExam,
        sessionsPerWeek,
        chaptersToStudy: totalChapters
      });
      
      setStep(3);
    } catch (err) {
      console.error('Failed to save profile:', err);
    }
    setLoading(false);
  };

  const handleStartSession = async () => {
    try {
      await api.post('/user/onboarding-complete');
      onClose();
      onNavigate('daily-loop');
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      onClose();
      onNavigate('daily-loop');
    }
  };

  const handleSendWhatsApp = async () => {
    const message = encodeURIComponent(
      `Hi! ${profile.childName} just set a study goal on StudyClaw: ${targetScore}% target for ${profile.board} Board Exam. Track their progress here → https://studyclaw.io`
    );
    
    const phone = parentPhone.replace(/\D/g, '');
    const waLink = `https://wa.me/${phone.startsWith('91') ? phone : '91' + phone}?text=${message}`;
    
    window.open(waLink, '_blank');
    
    try {
      await api.post('/user/onboarding-complete');
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
    }
    
    onClose();
    onNavigate('dashboard');
  };

  const handleSkip = async () => {
    try {
      await api.post('/user/onboarding-skip');
    } catch (err) {
      console.error('Failed to record skip:', err);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col bg-slate-900"
        data-testid="score-contract-modal"
      >
        {/* Header with progress */}
        <div className="flex-shrink-0 px-4 pt-4 pb-3 bg-slate-950/80 backdrop-blur-sm border-b border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                <Target className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-white">StudyClaw</span>
            </div>
            <span className="text-xs font-medium text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full">
              Step {step} of 3
            </span>
          </div>
          {/* Progress bar */}
          <div className="flex gap-1">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  i <= step ? 'bg-gradient-to-r from-violet-500 to-cyan-500' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pb-24">
          {/* Step 1: Profile Setup */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-5"
            >
              <h2 className="text-xl font-bold text-white mb-1">Set up your child&apos;s profile</h2>
              <p className="text-sm text-slate-400 mb-6">This helps us build a personalised readiness score for the right exam.</p>

              {/* Child's name */}
              <div className="mb-5">
                <label className="text-sm font-medium text-slate-300 mb-2 block">Child&apos;s name</label>
                <input
                  type="text"
                  value={profile.childName}
                  onChange={(e) => setProfile({ ...profile, childName: e.target.value })}
                  placeholder="e.g. Arya Sharma"
                  className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all"
                  data-testid="child-name-input"
                />
              </div>

              {/* Grade Selection */}
              <div className="mb-5">
                <label className="text-sm font-medium text-slate-300 mb-2 block">Class / Grade</label>
                <div className="grid grid-cols-5 gap-2">
                  {GRADES.map(grade => (
                    <button
                      key={grade}
                      onClick={() => setProfile({ ...profile, grade })}
                      className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
                        profile.grade === grade
                          ? 'bg-violet-500/20 border-2 border-violet-500 text-violet-300'
                          : 'bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid={`grade-${grade}-btn`}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              </div>

              {/* Board Selection */}
              <div className="mb-5">
                <label className="text-sm font-medium text-slate-300 mb-2 block">Board</label>
                <div className="flex flex-wrap gap-2">
                  {BOARDS.map(board => (
                    <button
                      key={board}
                      onClick={() => setProfile({ ...profile, board })}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                        profile.board === board
                          ? 'bg-violet-500/20 border-2 border-violet-500 text-violet-300'
                          : 'bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid={`board-${board.toLowerCase().replace(' ', '-')}-btn`}
                    >
                      {board}
                    </button>
                  ))}
                </div>
              </div>

              {/* Exam Date */}
              <div className="mb-5">
                <label className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-1">
                  Board exam start date
                  <span className="text-violet-400 cursor-help" title="We use this to calculate days remaining and set study pace">
                    <Info className="w-3.5 h-3.5" />
                  </span>
                </label>
                <input
                  type="date"
                  value={profile.examDate}
                  onChange={(e) => setProfile({ ...profile, examDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none transition-all"
                  data-testid="exam-date-input"
                />
              </div>

              <p className="text-center text-xs text-slate-500 mt-4">
                You can update any of this anytime in Settings
              </p>
            </motion.div>
          )}

          {/* Step 2: Target Score & Chapters */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-5"
            >
              <h2 className="text-xl font-bold text-white mb-1">Which chapters has school covered?</h2>
              <p className="text-sm text-slate-400 mb-4">
                Tick the chapters your child&apos;s school has <em>already taught</em>. Readiness scores are calculated <strong className="text-violet-400">only</strong> for these.
              </p>

              {/* Info banner */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-5">
                <p className="text-sm text-amber-300 flex items-start gap-2">
                  <span className="text-base">💡</span>
                  You&apos;ll get a weekly reminder to update this as school progresses through the term.
                </p>
              </div>

              {/* Target Score */}
              <div className="mb-6 bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-1">
                    Target score %
                    <span className="text-violet-400 cursor-help" title="We'll compare mock exam scores against this target">
                      <Info className="w-3.5 h-3.5" />
                    </span>
                  </label>
                  <span className="text-lg font-bold text-violet-400">{targetScore}%</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="100"
                  value={targetScore}
                  onChange={(e) => setTargetScore(parseInt(e.target.value))}
                  className="w-full accent-violet-500 h-2"
                  data-testid="target-score-slider"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Chapters by Subject */}
              <div className="space-y-4">
                {Object.entries(chapters).map(([subject, subjectChapters]) => (
                  subjectChapters.length > 0 && (
                    <div key={subject} className="border border-slate-700 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50">
                        <span className="font-semibold text-white flex items-center gap-2">
                          {subject === 'Mathematics' && '📐'}
                          {subject === 'Science' && '🔬'}
                          {subject === 'English' && '📖'}
                          {subject === 'Hindi' && '📚'}
                          {subject === 'Social Science' && '🌍'}
                          {!['Mathematics', 'Science', 'English', 'Hindi', 'Social Science'].includes(subject) && '📘'}
                          {subject}
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          getSubjectCoveredCount(subject) > 0 
                            ? 'bg-cyan-500/20 text-cyan-300' 
                            : 'bg-slate-700 text-slate-400'
                        }`}>
                          {getSubjectCoveredCount(subject)} / {getTotalChapterCount(subject)} covered
                        </span>
                      </div>
                      <div className="divide-y divide-slate-800">
                        {subjectChapters.map((chapter) => (
                          <button
                            key={chapter.number}
                            onClick={() => toggleChapter(subject, chapter.number)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/30 transition-colors text-left"
                            data-testid={`chapter-${subject}-${chapter.number}`}
                          >
                            <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all ${
                              (coveredChapters[subject] || []).includes(chapter.number)
                                ? 'bg-violet-500'
                                : 'border border-slate-600'
                            }`}>
                              {(coveredChapters[subject] || []).includes(chapter.number) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <span className={`text-sm ${
                              (coveredChapters[subject] || []).includes(chapter.number)
                                ? 'text-white'
                                : 'text-slate-400'
                            }`}>
                              {chapter.number}. {chapter.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Share with Parent */}
          {step === 3 && plan && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-5"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Here&apos;s how you&apos;ll hit {targetScore}%</h2>
                <p className="text-sm text-slate-400">Your personalized study plan</p>
              </div>

              {/* Plan Stats */}
              <div className="space-y-3 mb-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{plan.daysUntilExam}</p>
                    <p className="text-sm text-slate-400">days until exam</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Target className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{plan.sessionsPerWeek}</p>
                    <p className="text-sm text-slate-400">sessions per week recommended</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{plan.chaptersToStudy}</p>
                    <p className="text-sm text-slate-400">chapters marked as covered</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20 rounded-xl p-4 text-center">
                  <p className="text-white font-medium">Just 5 minutes a day</p>
                  <p className="text-xs text-slate-400 mt-1">Consistency beats cramming</p>
                </div>
              </div>

              {/* Parent WhatsApp */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-white">Share with your parent</span>
                </div>
                <p className="text-sm text-slate-400 mb-3">They&apos;ll get weekly updates on your progress.</p>
                <div className="flex gap-2">
                  <span className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-400 text-sm">+91</span>
                  <input
                    type="tel"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    placeholder="9876543210"
                    maxLength={10}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-green-500 outline-none"
                    data-testid="parent-phone-input"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Sticky Bottom Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800">
          {step === 1 && (
            <>
              <button
                onClick={handleStep1Continue}
                disabled={!profile.childName || !profile.examDate}
                className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
                data-testid="step1-continue-btn"
              >
                Continue → Add Chapters
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={handleSkip}
                className="w-full mt-2 text-slate-500 hover:text-slate-300 text-sm py-2 transition-all"
              >
                I&apos;ll do this later
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button
                onClick={handleStep2Continue}
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
                data-testid="step2-continue-btn"
              >
                {loading ? 'Saving...' : 'Save & Go to Dashboard →'}
              </button>
              <button
                onClick={() => setStep(1)}
                className="w-full mt-2 text-slate-500 hover:text-slate-300 text-sm py-2 transition-all"
              >
                ← Back
              </button>
            </>
          )}

          {step === 3 && (
            <>
              {parentPhone.length >= 10 ? (
                <button
                  onClick={handleSendWhatsApp}
                  className="w-full bg-green-600 hover:bg-green-500 text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 transition-all"
                  data-testid="send-whatsapp-btn"
                >
                  <Send className="w-5 h-5" />
                  Send on WhatsApp
                </button>
              ) : (
                <button
                  onClick={handleStartSession}
                  className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
                  data-testid="start-session-btn"
                >
                  Start My First Session
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={handleStartSession}
                className="w-full mt-2 text-slate-500 hover:text-slate-300 text-sm py-2 transition-all"
              >
                Skip for now
              </button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
